import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, Plus, Video, PhoneCall, Building, MapPin } from "lucide-react";
import { API, apiError } from "../lib/api";
import { PageHeader, Spinner, Empty } from "../components/common";
import { timeAgo } from "../lib/helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";

const TYPES = ["Call", "Video Call", "Office Meeting", "Site Visit"];
const STATUSES = ["Scheduled", "Completed", "Cancelled", "No Show"];
const statusColor = { Scheduled: "text-blue-400 bg-blue-500/10", Completed: "text-emerald-400 bg-emerald-500/10", Cancelled: "text-slate-400 bg-slate-500/10", "No Show": "text-red-400 bg-red-500/10" };

export default function Meetings() {
  const [meetings, setMeetings] = useState(null);
  const [leads, setLeads] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ lead_id: "", associate_id: "", date: "", time: "15:00", type: "Office Meeting", notes: "" });

  const load = () => API.get("/meetings").then((r) => setMeetings(r.data));
  useEffect(() => { load(); API.get("/leads").then((r) => setLeads(r.data)); API.get("/associates").then((r) => setAssociates(r.data)); }, []);

  const create = async () => {
    if (!form.lead_id || !form.date) { toast.error("Lead and date required"); return; }
    try { await API.post("/meetings", form); toast.success("Meeting scheduled"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (m, status) => { await API.put(`/meetings/${m.id}`, { status }); load(); toast.success(`Marked ${status}`); };

  if (!meetings) return <Spinner />;

  return (
    <div>
      <PageHeader title="Meetings" subtitle="Calls, video calls & office meetings." testid="meetings-header">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><button className="btn-gold px-4 py-2 flex items-center gap-2 text-sm" data-testid="new-meeting-btn"><Plus className="h-4 w-4" /> Schedule</button></DialogTrigger>
          <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
            <DialogHeader><DialogTitle>Schedule Meeting</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <select value={form.lead_id} onChange={(e) => setForm({ ...form, lead_id: e.target.value })} data-testid="meeting-lead" className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm">
                <option value="">Select lead…</option>{leads.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <select value={form.associate_id} onChange={(e) => setForm({ ...form, associate_id: e.target.value })} className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm">
                <option value="">Select associate…</option>{associates.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="meeting-date" className="bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm" />
                <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm" />
              </div>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
            </div>
            <DialogFooter><button onClick={create} className="btn-gold px-4 py-2 text-sm" data-testid="save-meeting-btn">Schedule</button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {meetings.length === 0 ? <Empty label="No meetings scheduled." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {meetings.map((m) => (
            <div key={m.id} className="mb-card p-4 animate-fade-up" data-testid={`meeting-${m.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-heading font-semibold text-slate-100">{m.lead_name || "Lead"}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColor[m.status]}`}>{m.status}</span>
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-1.5 mb-1">
                {m.type === "Video Call" ? <Video className="h-3.5 w-3.5" /> : m.type === "Call" ? <PhoneCall className="h-3.5 w-3.5" /> : <Building className="h-3.5 w-3.5" />} {m.type}
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {(m.date || "").slice(0, 10)} {m.time}</div>
              {m.status === "Scheduled" && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setStatus(m, "Completed")} className="flex-1 text-xs py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/25" data-testid={`complete-meeting-${m.id}`}>Complete</button>
                  <button onClick={() => setStatus(m, "Cancelled")} className="flex-1 text-xs py-1.5 rounded-lg border border-slate-700 text-slate-400">Cancel</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
